import { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface DocumentItem {
  url: string;
  fileName: string;
  fieldName: string;
  index: number;
  uploadedAt?: string;
}

interface DocumentGalleryViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documents: DocumentItem[];
  initialIndex: number;
}

export function DocumentGalleryViewer({
  isOpen,
  onClose,
  documents,
  initialIndex,
}: DocumentGalleryViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset to initial index when modal opens or initialIndex changes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const currentDocument = documents[currentIndex];

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : documents.length - 1));
  }, [documents.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < documents.length - 1 ? prev + 1 : 0));
  }, [documents.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, onClose]);

  if (!isOpen || !currentDocument) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors z-50"
        onClick={onClose}
      >
        <X className="h-6 w-6 text-white" />
      </button>

      {/* Navigation buttons */}
      {documents.length > 1 && (
        <>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors z-50"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            <ChevronLeft className="h-8 w-8 text-white" />
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors z-50"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            <ChevronRight className="h-8 w-8 text-white" />
          </button>
        </>
      )}

      {/* Main content area */}
      <div
        className="flex flex-col items-center w-full max-w-6xl px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main image with caption */}
        <div className="relative w-full flex flex-col items-center mb-6">
          <img
            src={currentDocument.url}
            alt={currentDocument.fileName}
            className="max-h-[70vh] max-w-full object-contain rounded shadow-2xl"
          />

          {/* Filename caption */}
          <div className="mt-4 text-center">
            <p className="text-white text-lg font-medium">
              {currentDocument.fileName}
            </p>
            {currentDocument.uploadedAt && (
              <p className="text-white/70 text-sm mt-1">
                Uploaded: {new Date(currentDocument.uploadedAt).toLocaleDateString()} at{' '}
                {new Date(currentDocument.uploadedAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
            {documents.length > 1 && (
              <p className="text-white/50 text-sm mt-1">
                {currentIndex + 1} of {documents.length}
              </p>
            )}
          </div>
        </div>

        {/* Thumbnail strip */}
        {documents.length > 1 && (
          <div className="w-full overflow-x-auto py-4">
            <div className="flex gap-4 justify-center min-w-min px-6">
              {documents.map((doc, index) => (
                <button
                  key={`${doc.fieldName}-${doc.index}`}
                  className={`relative flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden transition-all ${
                    index === currentIndex
                      ? 'border-primary ring-2 ring-primary scale-110'
                      : 'border-white/30 hover:border-white/60'
                  }`}
                  onClick={() => setCurrentIndex(index)}
                >
                  <img
                    src={doc.url}
                    alt={doc.fileName}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay for non-active thumbnails */}
                  {index !== currentIndex && (
                    <div className="absolute inset-0 bg-black/40 hover:bg-black/20 transition-colors" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Keyboard hints */}
      {documents.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
          Use arrow keys to navigate • ESC to close
        </div>
      )}
    </div>
  );
}
